import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CommonApiResultError } from "shared/networking/common_api_result";
import HttpError from "shared/networking/http_error";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ErrorListener = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const queryUnsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        handleError(event.action.error);
      }
    });

    const mutationUnsubscribe = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event.type === "updated" && event.action.type === "error") {
          handleError(event.action.error);
        }
      });

    return () => {
      queryUnsubscribe();
      mutationUnsubscribe();
    };
  }, [queryClient]);

  const handleError = (error: Error) => {
    if (error instanceof HttpError && error.code === "401") {
      const returnUrl = encodeURIComponent(window.location.pathname);
      document.location.href = `/account/login?returnUrl=${returnUrl}`;
      return;
    }

    if (error instanceof CommonApiResultError && error.errorCode === 401) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      document.location.href = `/account/login?returnUrl=${returnUrl}`;
      return;
    }

    if (error instanceof CommonApiResultError) {
      let message = error.message + "\n";
      message += formatErrors(error.messageParameter);
      setErrorMessage(message);
    } else {
      setErrorMessage(
        `An unexpected error occurred.\nDetails: ${error.message}`,
      );
    }
  };

  const handleClose = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const formatErrors = (obj: any) => {
    if (!obj || typeof obj !== "object") return "";

    return Object.entries(obj)
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join("\n");
  };

  if (!errorMessage) return null;

  return (
    <Dialog open={true} onClose={handleClose} disableEscapeKeyDown={true}>
      <DialogTitle>{t("Error")}</DialogTitle>
      <DialogContent>
        {errorMessage.split("\n").map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          {t("Close_Button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorListener;
